import AsyncStorage from '@react-native-async-storage/async-storage';
import { createLogger } from '@/lib/logger';

const log = createLogger('CACHE');
const CACHE_PREFIX = '@duel_cache:';

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

/**
 * Simple TTL cache backed by AsyncStorage.
 * All methods are safe — errors fall through to null/void.
 */

export async function get<T>(key: string): Promise<T | null> {
  try {
    const raw = await AsyncStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const entry: CacheEntry<T> = JSON.parse(raw);
    if (Date.now() > entry.expiresAt) {
      // Expired — remove async, don't await
      AsyncStorage.removeItem(CACHE_PREFIX + key).catch(() => {});
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

export async function set<T>(key: string, data: T, ttlMs: number): Promise<void> {
  try {
    const entry: CacheEntry<T> = { data, expiresAt: Date.now() + ttlMs };
    await AsyncStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
  } catch (e) {
    log.warn('cache set failed', { key, error: e });
  }
}

export async function invalidate(key: string): Promise<void> {
  try {
    await AsyncStorage.removeItem(CACHE_PREFIX + key);
  } catch {}
}

export async function invalidatePrefix(prefix: string): Promise<void> {
  try {
    const keys = await AsyncStorage.getAllKeys();
    const target = CACHE_PREFIX + prefix;
    const toRemove = keys.filter((k) => k.startsWith(target));
    if (toRemove.length > 0) {
      await AsyncStorage.multiRemove(toRemove);
    }
  } catch {}
}
